import { MutableRefObject, useMemo } from 'react';

import { Button, DatePicker, Input, Radio, RadioGroup, Select, Text, TypeSelectOption } from '@thefarmersfront/kitchen';
import { findItemByField } from '@thefarmersfront/kitchen-utensil';

import { useShallow } from 'zustand/react/shallow';

import { FormField, ModalSection } from '~/components/common';

import { useKakaoPostcodeSearch } from '~/hooks/common';
import { useStockReleaseStore, useUserInfoStore } from '~/store';
import { formatDate, formatNumber, getDateByOffset, validateNumberAndHyphenOnly } from '~/utils';

import { DEFAULT_INPUT_STATUS, STOCK_RELEASE_PACKING_METHOD_ALLOW_CODES, STOCK_TOOLTIP_MESSAGE } from '~/constants';

import { TypeStockReleaseRegisterErrorFieldRefs } from '~/types/stock.types';

import styles from './StockReleaseRegisterSecondStepContent.module.scss';

/**
 * [재고관리 > 반출요청관리] 반출요청등록 모달 > STEP 2 > 반출 예정 정보
 */
const StockReleaseRegisterDetailInfo = ({
  errorFieldRefs,
  options,
}: {
  errorFieldRefs: MutableRefObject<TypeStockReleaseRegisterErrorFieldRefs>;
  options: Record<string, TypeSelectOption<string>[]> | null;
}) => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermission3PL = hasPermission([{ ownerType: '3PL' }]);

  const { cluster, requestGoods, detailInfo, detailInfoState, setDetailInfo, resetPalletInfo, setRegisterInfoState } =
    useStockReleaseStore(
      useShallow((state) => ({
        cluster: state.cluster,
        requestGoods: state.requestGoods,
        detailInfo: state.detailInfo,
        detailInfoState: state.detailInfoState,
        setDetailInfo: state.actions.setDetailInfo,
        resetPalletInfo: state.actions.resetPalletInfo,
        setRegisterInfoState: state.actions.setRegisterInfoState,
      }))
    );

  const isPackingMethodAllowed = useMemo(
    () => hasPermission3PL || STOCK_RELEASE_PACKING_METHOD_ALLOW_CODES.includes(detailInfo?.method ?? ''),
    [detailInfo?.method, hasPermission3PL]
  );

  // 반출 희망일 선택 범위
  const minDate = getDateByOffset(3, 'day');
  const maxDate = getDateByOffset(14, 'day');

  const { handleZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (zipCode, address) => {
      setDetailInfo({ zipCode, address });
      setRegisterInfoState({ detailInfoState: { zipCode: DEFAULT_INPUT_STATUS, address: DEFAULT_INPUT_STATUS } });
    },
  });

  return (
    <ModalSection className={styles['detail-info']} title="반출 예정 정보">
      <FormField label="반출 출발지 센터" contentAlign="center" contents={<Text size="large">{cluster?.text}</Text>} />
      <FormField
        ref={(el) => (errorFieldRefs.current['detailInfo.expectedDate'] = el)}
        label="반출 희망일"
        required
        tooltip={{
          description: STOCK_TOOLTIP_MESSAGE.RELEASE_REQUEST_DATE,
        }}
        contents={
          <DatePicker
            size="small"
            placeholder="날짜 선택"
            maxWidth="228px"
            portalTarget={document.body}
            shouldCloseOnNotInView
            minDate={minDate}
            maxDate={maxDate}
            value={detailInfo?.expectedDate ? new Date(detailInfo?.expectedDate) : null}
            status={detailInfoState.expectedDate.status}
            validationMessage={detailInfoState.expectedDate.message}
            onChange={(expectedDate) => {
              setDetailInfo({ expectedDate: formatDate(expectedDate, 'YYYY-MM-DD') ?? '' });
              setRegisterInfoState({ detailInfoState: { expectedDate: DEFAULT_INPUT_STATUS } });
            }}
            onFocus={() => setRegisterInfoState({ detailInfoState: { expectedDate: DEFAULT_INPUT_STATUS } })}
          />
        }
      />
      <FormField
        label="반출 재고"
        contentAlign="center"
        contents={
          requestGoods.length > 0 && (
            <Text size="large">{`${requestGoods[0].skuName}${
              requestGoods.length > 1 ? ` 외 ${requestGoods.length - 1}종` : ''
            }, 총 ${formatNumber(
              requestGoods?.reduce(
                (acc, { availableRequestQuantity = 0, defectiveRequestQuantity = 0 }) =>
                  acc + availableRequestQuantity + defectiveRequestQuantity,
                0
              )
            )}개`}</Text>
          )
        }
      />
      <FormField
        label="반출 방식"
        required
        tooltip={{
          description: STOCK_TOOLTIP_MESSAGE.RELEASE_METHOD,
        }}
        contents={
          <Select
            ref={(el) => (errorFieldRefs.current['detailInfo.method'] = el)}
            size="small"
            placeholder="선택"
            maxWidth="148px"
            dropdownPortalTarget={document.body}
            shouldCloseOnNotInView
            options={options?.vendorReleaseMethod || []}
            value={findItemByField(options?.vendorReleaseMethod ?? [], { value: detailInfo?.method ?? '' })}
            status={detailInfoState.method.status}
            validationMessage={detailInfoState.method.message}
            onChange={(_: unknown, method: TypeSelectOption<string>) => {
              setDetailInfo({ method: method.value, packingMethod: undefined });
              resetPalletInfo();
              setRegisterInfoState({ detailInfoState: { packingMethod: DEFAULT_INPUT_STATUS } });
            }}
            onClick={() => setRegisterInfoState({ detailInfoState: { method: DEFAULT_INPUT_STATUS } })}
          />
        }
      />
      <FormField
        label="반출 도착지 연락처"
        required
        contents={
          <Input
            ref={(el) => (errorFieldRefs.current['detailInfo.phoneNumber'] = el)}
            type="text"
            size="small"
            maxWidth="289px"
            placeholder="- 포함 입력"
            status={detailInfoState.phoneNumber.status}
            validationMessage={detailInfoState.phoneNumber.message}
            value={detailInfo?.phoneNumber ?? ''}
            onChange={(e) => {
              const phoneNumber = e.target.value;
              if (validateNumberAndHyphenOnly(phoneNumber)) {
                setDetailInfo({ phoneNumber });
                setRegisterInfoState({ detailInfoState: { phoneNumber: DEFAULT_INPUT_STATUS } });
              }
            }}
            onBlur={(e) => setDetailInfo({ phoneNumber: e.target.value.trim() })}
            onClick={() => setRegisterInfoState({ detailInfoState: { phoneNumber: DEFAULT_INPUT_STATUS } })}
          />
        }
      />
      <FormField
        ref={(el) => (errorFieldRefs.current['detailInfo.zipCode'] = el)}
        label="반출 도착지 우편번호"
        required
        contents={
          <>
            <Input
              type="text"
              size="small"
              maxWidth="236px"
              disabled
              value={detailInfo?.zipCode ?? ''}
              status={detailInfoState.zipCode.status}
              validationMessage={detailInfoState.zipCode.message}
            />
            <Button
              size="small"
              color="secondary"
              onClick={() => {
                handleZipCodeSearchOpen();
                setRegisterInfoState({
                  detailInfoState: { zipCode: DEFAULT_INPUT_STATUS, address: DEFAULT_INPUT_STATUS },
                });
              }}
            >
              검색
            </Button>
          </>
        }
        contentsClassName={styles['address-container']}
      />
      <FormField
        label="반출 도착지 기본주소"
        required
        contents={
          <Input
            ref={(el) => (errorFieldRefs.current['detailInfo.address'] = el)}
            type="text"
            size="small"
            maxWidth="289px"
            disabled
            value={detailInfo?.address ?? ''}
            status={detailInfoState.address.status}
            validationMessage={detailInfoState.address.message}
          />
        }
      />
      <FormField
        label="반출 도착지 상세주소"
        contents={
          <Input
            ref={(el) => (errorFieldRefs.current['detailInfo.detailAddress'] = el)}
            type="text"
            size="small"
            maxWidth="770px"
            placeholder="입력"
            count
            maxLength={50}
            value={detailInfo?.detailAddress ?? ''}
            onChange={(e) => setDetailInfo({ detailAddress: e.target.value })}
            onBlur={(e) => setDetailInfo({ detailAddress: e.target.value.trim() })}
          />
        }
      />
      <FormField
        label="반출 출발지 센터 정보"
        contentAlign="center"
        contents={<Text size="large">반출 패킹 완료 후 업데이트됩니다.</Text>}
      />
      {isPackingMethodAllowed && (
        <FormField
          ref={(el) => (errorFieldRefs.current['detailInfo.packingMethod'] = el)}
          label="패킹 방식"
          required
          contentAlign="center"
          contents={
            <RadioGroup
              name="packingMethod"
              size="medium"
              value={detailInfo?.packingMethod}
              status={detailInfoState.packingMethod.status}
              validationMessage={detailInfoState.packingMethod.message}
              onChange={(e) => {
                setDetailInfo({ packingMethod: e.target.value });
                resetPalletInfo();
                setRegisterInfoState({ detailInfoState: { packingMethod: DEFAULT_INPUT_STATUS } });
              }}
            >
              {(options?.vendorReleasePackingMethod || []).map(({ value, text }) => (
                <Radio key={value} label={text} value={value} />
              ))}
            </RadioGroup>
          }
        />
      )}
    </ModalSection>
  );
};

export default StockReleaseRegisterDetailInfo;
